/**
 * Formatage des valeurs affichées à l'utilisateur : heures, durées, poids de
 * fichiers et accords pluriels. Toutes les chaînes produites sont en français.
 */

const CLOCK_FORMATTER = new Intl.DateTimeFormat("fr-BE", {
  hour: "2-digit",
  minute: "2-digit",
});

const BYTES_FORMATTER = new Intl.NumberFormat("fr-BE", {
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
});

const BYTE_UNITS = ["octets", "Ko", "Mo", "Go", "To"] as const;

/** Formate un horodatage en heure locale, ex. « 17 h 08 ». */
export function formatClock(timestamp: number): string {
  // Le séparateur produit par l'environnement (« : », « h »…) varie selon la
  // plateforme : on extrait les parties plutôt que de remplacer une chaîne.
  const parts = CLOCK_FORMATTER.formatToParts(new Date(timestamp));
  const hour = parts.find((part) => part.type === "hour")?.value ?? "00";
  const minute = parts.find((part) => part.type === "minute")?.value ?? "00";
  return `${hour} h ${minute}`;
}

/** Formate une durée en millisecondes au format « mm:ss », ex. « 01:24 ». */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

/** Formate une taille en octets de façon lisible, ex. « 24,3 Mo ». */
export function formatBytes(bytes: number): string {
  if (bytes <= 0) return "0 octet";

  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), BYTE_UNITS.length - 1);
  const unit = BYTE_UNITS[exponent] ?? "octets";
  const value = bytes / 1024 ** exponent;
  return `${BYTES_FORMATTER.format(value)} ${unit}`;
}

/** Accorde un mot au singulier ou au pluriel selon un compte, ex. « 3 souvenirs ». */
export function pluralize(count: number, singular: string, plural?: string): string {
  const word = count === 1 ? singular : (plural ?? `${singular}s`);
  return `${count} ${word}`;
}
